import React, { useEffect, useState } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { RefreshCw, TrendingUp, TrendingDown } from "lucide-react";

interface SignalEntry {
  symbol: string;
  price: number;
  entry_price: number;
  reason: string;
  strategy: string;
  side: string;
  interval: string;
  is_hit: boolean;
  updated_at: string;
}

export const SignalAutoSignals: React.FC = () => {
  const [signals, setSignals] = useState<SignalEntry[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchSignals = async () => {
    try {
      const res = await fetch('/signal/auto_signals?hits_only=true');
      const data = await res.json();
      setSignals(data.signals || []);
    } catch (err) {
      console.error('Failed to fetch auto signals:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSignals();
    const interval = setInterval(fetchSignals, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-black tracking-tight">
          Autobot 실시간 신호
        </h2>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-[10px] font-black text-success border-success/30 bg-success/5">
            {signals.length}개 타점
          </Badge>
          <Button variant="ghost" size="sm" onClick={fetchSignals} className="h-8 text-xs font-bold">
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
            새로고침
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading && signals.length === 0 ? (
            <div className="p-12 text-center text-xs text-muted-foreground font-medium">
              신호 데이터를 불러오는 중...
            </div>
          ) : signals.length === 0 ? (
            <div className="p-12 text-center text-xs text-muted-foreground font-medium">
              현재 포착된 신호가 없습니다.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="text-[10px] font-bold text-muted-foreground uppercase">
                    <TableHead className="pl-4">심볼</TableHead>
                    <TableHead>전략</TableHead>
                    <TableHead>방향</TableHead>
                    <TableHead>봉</TableHead>
                    <TableHead className="text-right">현재가</TableHead>
                    <TableHead className="text-right">진입가</TableHead>
                    <TableHead>근거</TableHead>
                    <TableHead className="text-right">갱신</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {signals.map(sig => (
                    <TableRow key={sig.symbol} className="text-xs bg-success/5">
                      <TableCell className="pl-4">
                        <span className="font-black text-foreground">{sig.symbol}</span>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-[9px] font-bold border-primary/20 text-primary">
                          {sig.strategy || '-'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`text-[9px] font-black px-1.5 py-0.5 ${sig.side === 'LONG' ? 'bg-success/10 text-success border-success/20' : 'bg-danger/10 text-danger border-danger/20'}`}>
                          {sig.side === 'LONG' ? <TrendingUp className="w-2.5 h-2.5 mr-0.5 inline" /> : <TrendingDown className="w-2.5 h-2.5 mr-0.5 inline" />}
                          {sig.side}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-bold text-muted-foreground">{sig.interval || '-'}</TableCell>
                      <TableCell className="text-right font-black tabular-nums">
                        ${sig.price?.toLocaleString() ?? '-'}
                      </TableCell>
                      <TableCell className="text-right font-black tabular-nums text-primary">
                        ${sig.entry_price?.toLocaleString() ?? '-'}
                      </TableCell>
                      <TableCell className="max-w-[200px]">
                        <span className="text-[10px] text-muted-foreground font-medium leading-tight block truncate" title={sig.reason}>
                          {sig.reason || '-'}
                        </span>
                      </TableCell>
                      <TableCell className="text-right text-[10px] text-muted-foreground font-bold tabular-nums">
                        {sig.updated_at || '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
